import { useMemo } from 'react';
import { IngredientWithDosing } from '../../../components/shoppingCart/types';
import { OrderItem } from '../../../model/order/OrderItem';
import { useAllIngredients } from './useAllIngredients';
import { Commerce } from '@kotipizzagroup/kotipizza-shared-types';
import { ProductLockEnum } from '@kotipizzagroup/kotipizza-products-api-client';

export const useIngredientsForItem = (
  item: OrderItem | null
): {
  ingredients: IngredientWithDosing[];
  isLoading: boolean;
} => {
  const { ingredients: allIngredients, isLoading } = useAllIngredients();

  const itemSizeId = item?.sizeId;

  return useMemo(() => {
    if (!item) {
      return {
        isLoading: true,
        ingredients: [],
      };
    }

    const ingredients = allIngredients
      .filter((ingredient) => ingredient.productSizeId === item.sizeId)
      .map((ingredient) => {
        const isDisabled =
          item.disabledIngredients.some(
            (disabled) => ingredient.ingredientID === disabled.ingredientID
          ) || ingredient.isOutOfStock;

        if (isDisabled) {
          return null;
        }

        const itemIngredient = item.recipe.find(
          (itemIngredient) => itemIngredient.ingredientID === ingredient.ingredientID
        );

        if (!itemIngredient) {
          // Recipe ingredients can't be added as extra to products that don't include them in original recipe.
          if (ingredient.isRecipeOnlyIngredient) return null;

          /*
            Apply campaign restrictions for editing products if restrictedModification is set.
            https://dev.azure.com/kotipizza/Kotipizza/_wiki/wikis/Kotipizza.wiki/604/Campaign-product-modification-restrictions
          */
          if (!item.restrictedModification && item.modificationLocked === ProductLockEnum.NoLock)
            return ingredient;

          // Campaign restrictions don't allow changing base sauce.
          if (ingredient.ingredientCategoryID === Commerce.BaseIngredientCategory.BASE_SAUCE) {
            return null;
          }

          // Only Rulla, Kotzone, Lankku -products can change top sauce.
          if (ingredient.ingredientCategoryID === Commerce.IngredientCategory.SAUCE) {
            const freeTopSauceProductCategories = [
              Commerce.ProductCategory.RULLA,
              Commerce.ProductCategory.KOTZONE,
              Commerce.ProductCategory.LANKKU,
            ];

            if (!freeTopSauceProductCategories.includes(item.productCategoryId)) {
              return null;
            }
          }

          return ingredient;
        }

        return {
          ...ingredient,
          minQuantity: itemIngredient.minQuantity,
          maxQuantity: itemIngredient.maxQuantity,
        };
      })
      .filter(Boolean) as IngredientWithDosing[];

    return {
      ingredients,
      isLoading,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    allIngredients,
    isLoading,
    itemSizeId,
    item?.hasExtraIngredients,
    item?.restrictedModification,
  ]);
};
